import { Field, Form, Formik } from 'formik';
import React, { useCallback } from 'react';
import Pluralize from 'react-pluralize';

import { useToasts } from '@sbu/contexts.toasts';
import { DynamicFeatureFlag, useFeatureFlag } from '@sbu/hooks.featureflag';
import { EventPropertyName, TrackEventName } from '@sbu/utils.tracking';
import {
  Button,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
} from '@sumologic/yama-ds-components';

import {
  DEFAULT_ENTITY_CRITICALITY,
  EntityCriticalitySelect,
} from 'app/Entities/EntityCriticalitySelect';
import {
  BulkUpdateEntityCriticalityOperationMutation as BulkUpdateEntityCriticalityOperation,
  BulkUpdateEntityCriticalityOperationMutationVariables as BulkUpdateEntityCriticalityOperationVariables,
} from 'core/graphql';
import { useMutation, useTrackEvent } from 'shared/hooks';

import { BULK_UPDATE_ENTITY_CRITICALITY_MUTATION } from './mutations';

const CRITICALITY_FIELD_NAME = 'criticality';

interface EntitiesCriticalityEditorProps {
  entityIds: string[];
  onClose: () => void;
}

export const EntitiesCriticalityEditor = ({
  entityIds,
  onClose,
}: EntitiesCriticalityEditorProps) => {
  const { length } = entityIds;
  const isEntitiesListTableEnabled = useFeatureFlag(
    DynamicFeatureFlag.EnableEntitiesListTable
  );

  const [updateCriticality, { loading }] = useMutation<
    BulkUpdateEntityCriticalityOperation,
    BulkUpdateEntityCriticalityOperationVariables
  >(BULK_UPDATE_ENTITY_CRITICALITY_MUTATION);

  const { addSuccess } = useToasts();

  const trackEvent = useTrackEvent();

  const submitUpdateCriticality = useCallback(
    async (value: string) => {
      // Perform the update.
      const result = await updateCriticality({
        variables: {
          entityIds,
          criticality: value === DEFAULT_ENTITY_CRITICALITY ? null : value,
        },
      });

      if (result && !result.errors) {
        // Notify user.
        addSuccess(
          'The Entity Criticality values have been updated successfully.'
        );
      }

      if (isEntitiesListTableEnabled) {
        trackEvent(TrackEventName.ENTITIES_BULK_ACTION, {
          [EventPropertyName.ENTITY_COUNT]: entityIds.length,
          [EventPropertyName.ACTION]: 'ChangeCriticality',
        });
      }

      // Close the dialog.
      onClose();
    },
    [
      addSuccess,
      entityIds,
      onClose,
      updateCriticality,
      isEntitiesListTableEnabled,
      trackEvent,
    ]
  );

  // A validation schema is not defined because the criticality is not empty in
  // the view.
  return (
    <Dialog
      onClose={onClose}
      open={true}
      maxWidth='sm'
      fullWidth={true}
      aria-labelledby='entities-criticality-editor-dialog-title'
    >
      <DialogTitle id='entities-criticality-editor-dialog-title'>
        Change Criticality for {length.toLocaleString()}{' '}
        <Pluralize
          count={length}
          plural='Entities'
          showCount={false}
          singular='Entity'
        />
      </DialogTitle>
      <Formik
        initialValues={{
          [CRITICALITY_FIELD_NAME]: DEFAULT_ENTITY_CRITICALITY,
        }}
        onSubmit={(values) =>
          submitUpdateCriticality(values[CRITICALITY_FIELD_NAME])
        }
      >
        {() => (
          <Form>
            <DialogContent
              sx={{
                width: 500,
              }}
            >
              <Field name={CRITICALITY_FIELD_NAME}>
                {({ field: { value }, form: { setFieldValue } }) => (
                  <EntityCriticalitySelect
                    disabled={loading}
                    onValueChange={(value) =>
                      setFieldValue(CRITICALITY_FIELD_NAME, value)
                    }
                    value={value}
                  />
                )}
              </Field>
            </DialogContent>
            <DialogActions>
              <Button color='secondary' onClick={() => onClose()}>
                Cancel
              </Button>
              <Button disabled={loading} loading={loading} type='submit'>
                Confirm
              </Button>
            </DialogActions>
          </Form>
        )}
      </Formik>
    </Dialog>
  );
};
